/**
 * Database Cleanup Utilities
 * เครื่องมือสำหรับทำความสะอาดฐานข้อมูลโดยตรง
 */

import { doc, getDoc, updateDoc, arrayRemove, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * ทำความสะอาดรูปซ้ำในฐานข้อมูลโดยตรง
 * @param {string} girlId - ID ของผู้ใช้
 * @returns {Promise<{cleanedCount: number, duplicates: Array}>}
 */
export const cleanupDatabaseDuplicates = async (girlId) => {
  try {
    console.log('🧹 cleanupDatabaseDuplicates started:');
    console.log('- Girl ID:', girlId);
    
    const girlRef = doc(db, 'girls', girlId);
    const girlDoc = await getDoc(girlRef);
    
    if (!girlDoc.exists()) {
      console.log('❌ Girl document does not exist');
      return { cleanedCount: 0, duplicates: [] };
    }
    
    const girlData = girlDoc.data();
    const images = girlData.images || [];
    const pendingImages = girlData.pendingImages || [];
    const rejectedImages = girlData.rejectedImages || [];
    
    console.log('📊 Current Data:', {
      imagesCount: images.length,
      pendingImagesCount: pendingImages.length,
      rejectedImagesCount: rejectedImages.length
    });
    
    // หารูปซ้ำใน pending images ที่ซ้ำกับ approved images
    const duplicatePendingImages = [];
    const cleanedPendingImages = [];
    
    pendingImages.forEach((pendingImg, index) => {
      const isDuplicate = images.some(approvedImg => 
        approvedImg.fileHash === pendingImg.fileHash || 
        approvedImg.url === pendingImg.url ||
        (approvedImg.originalName === pendingImg.originalName && 
         approvedImg.originalSize === pendingImg.originalSize)
      );
      
      if (isDuplicate) {
        console.log(`❌ Found duplicate pending image:`, {
          index,
          id: pendingImg.id,
          fileName: pendingImg.fileName,
          originalName: pendingImg.originalName,
          url: pendingImg.url,
          fileHash: pendingImg.fileHash
        });
        duplicatePendingImages.push(pendingImg);
      } else {
        cleanedPendingImages.push(pendingImg);
      }
    });
    
    // หารูปซ้ำใน rejected images ที่ซ้ำกับ approved images
    const duplicateRejectedImages = [];
    const cleanedRejectedImages = [];
    
    rejectedImages.forEach((rejectedImg, index) => {
      const isDuplicate = images.some(approvedImg => 
        approvedImg.fileHash === rejectedImg.fileHash || 
        approvedImg.url === rejectedImg.url ||
        (approvedImg.originalName === rejectedImg.originalName && 
         approvedImg.originalSize === rejectedImg.originalSize)
      );
      
      if (isDuplicate) {
        console.log(`❌ Found duplicate rejected image:`, {
          index,
          id: rejectedImg.id,
          fileName: rejectedImg.fileName,
          originalName: rejectedImg.originalName,
          url: rejectedImg.url,
          fileHash: rejectedImg.fileHash
        });
        duplicateRejectedImages.push(rejectedImg);
      } else {
        cleanedRejectedImages.push(rejectedImg);
      }
    });
    
    const totalDuplicates = duplicatePendingImages.length + duplicateRejectedImages.length;
    
    if (totalDuplicates > 0) {
      console.log('💾 Updating database to remove duplicates...');
      
      // ลบรูปซ้ำออกจากฐานข้อมูล
      const updates = {};
      
      if (duplicatePendingImages.length > 0) {
        updates.pendingImages = cleanedPendingImages;
        console.log(`- Removing ${duplicatePendingImages.length} duplicate pending images`);
      }
      
      if (duplicateRejectedImages.length > 0) {
        updates.rejectedImages = cleanedRejectedImages;
        console.log(`- Removing ${duplicateRejectedImages.length} duplicate rejected images`);
      }
      
      updates.lastUpdated = new Date().toISOString();
      
      await updateDoc(girlRef, updates);
      
      console.log('✅ Database updated successfully');
      
      // ตรวจสอบผลลัพธ์
      const updatedGirlDoc = await getDoc(girlRef);
      const updatedGirlData = updatedGirlDoc.data();
      
      console.log('📊 Final Data:', {
        imagesCount: updatedGirlData.images?.length || 0,
        pendingImagesCount: updatedGirlData.pendingImages?.length || 0,
        rejectedImagesCount: updatedGirlData.rejectedImages?.length || 0
      });
    } else {
      console.log('✅ No duplicates found');
    }
    
    return {
      cleanedCount: totalDuplicates,
      duplicates: [
        ...duplicatePendingImages.map(dup => ({ ...dup, type: 'pending' })),
        ...duplicateRejectedImages.map(dup => ({ ...dup, type: 'rejected' }))
      ]
    };
  } catch (error) {
    console.error('💥 Error cleaning database duplicates:', error);
    return { cleanedCount: 0, duplicates: [] };
  }
};

/**
 * ทำความสะอาดรูปซ้ำสำหรับผู้ใช้ทั้งหมด
 * @returns {Promise<{totalCleaned: number, userResults: Array}>}
 */
export const cleanupAllDatabaseDuplicates = async () => {
  try {
    console.log('🧹 cleanupAllDatabaseDuplicates started');
    
    // ดึงข้อมูลผู้ใช้ทั้งหมด
    const usersSnapshot = await getDocs(collection(db, 'girls'));
    const results = [];
    let totalCleaned = 0;
    
    for (const userDoc of usersSnapshot.docs) {
      const userId = userDoc.id;
      const result = await cleanupDatabaseDuplicates(userId);
      
      if (result.cleanedCount > 0) {
        results.push({
          userId,
          cleanedCount: result.cleanedCount,
          duplicates: result.duplicates
        });
        totalCleaned += result.cleanedCount;
      }
    }
    
    console.log(`✅ Total cleaned ${totalCleaned} duplicate images across all users`);
    
    return {
      totalCleaned,
      userResults: results
    };
  } catch (error) {
    console.error('💥 Error cleaning all database duplicates:', error);
    return { totalCleaned: 0, userResults: [] };
  }
};
